import { useState } from 'react';
import {
  Activity,
  Award,
  BookOpen,
  Calendar,
  Compass,
  FlaskConical,
  GraduationCap,
  Grid3X3,
  Languages,
  Laptop,
  MessageSquare,
  Palette,

  Quote,
  School,
  TrendingUp,
  User,
} from 'lucide-react';
import { GRADE_TONE_CLASSES } from '@/constants/grading';
import { cn } from '@/lib/utils';
import { formatInstituteDate } from '@/features/teacher/utils';
import type { StudentProfileDetails } from './profileData';

interface ProfileAcademicDetailsTabProps {
  details: StudentProfileDetails;
  /** Whose record this is, when the reader is not the child themselves. */
  childName?: string;
}

type AcademicSubTab = 'Term Exams' | 'Unit Tests' | 'Assignments';

function getSubjectIconTheme(name: string) {
  const norm = name.toLowerCase();
  if (norm.includes('math')) {
    return {
      icon: <Grid3X3 className="h-4 w-4" />,
      box: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-800',
    };
  }
  if (norm.includes('sci') || norm.includes('phy') || norm.includes('chem') || norm.includes('bio')) {
    return {
      icon: <FlaskConical className="h-4 w-4" />,
      box: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/80 dark:border-emerald-800',
    };
  }
  if (norm.includes('soc') || norm.includes('hist') || norm.includes('geo')) {
    return {
      icon: <Compass className="h-4 w-4" />,
      box: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-400 border border-cyan-200/80 dark:border-cyan-800',
    };
  }
  if (norm.includes('telugu') || norm.includes('hindi') || norm.includes('lang')) {
    return {
      icon: <Languages className="h-4 w-4" />,
      box: 'bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200/80 dark:border-amber-800',
    };
  }
  if (norm.includes('comp') || norm.includes('tech') || norm.includes('it')) {
    return {
      icon: <Laptop className="h-4 w-4" />,
      box: 'bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/80 dark:border-purple-800',
    };
  }
  if (norm.includes('art') || norm.includes('craft')) {
    return {
      icon: <Palette className="h-4 w-4" />,
      box: 'bg-pink-50 text-pink-600 dark:bg-pink-950/60 dark:text-pink-400 border border-pink-200/80 dark:border-pink-800',
    };
  }
  if (norm.includes('phys') || norm.includes('pe') || norm.includes('sport')) {
    return {
      icon: <Activity className="h-4 w-4" />,
      box: 'bg-teal-50 text-teal-600 dark:bg-teal-950/60 dark:text-teal-400 border border-teal-200/80 dark:border-teal-800',
    };
  }
  return {
    icon: <BookOpen className="h-4 w-4" />,
    box: 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200/80 dark:border-blue-800',
  };
}

export function ProfileAcademicDetailsTab({ details, childName }: ProfileAcademicDetailsTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<AcademicSubTab>('Term Exams');

  const examProgressList =
    activeSubTab === 'Term Exams'
      ? details.termExams
      : activeSubTab === 'Unit Tests'
        ? details.unitTests
        : details.assignments;

  return (
    <div className="flex flex-col gap-5 animate-fade-in">
      {/* 1. Top Card: Academic Details with Print & 4 KPI cards */}
      <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-4 shadow-xs flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-white/10 pb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/70 dark:border-purple-800/60 shrink-0">
              <BookOpen className="h-4.5 w-4.5" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Academic Details
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                View {childName ? `${childName}'s` : 'your'} class, section, subjects and academic
                progress.
              </p>
            </div>
          </div>

          {/* <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-bold border border-slate-200 dark:border-slate-700 shadow-2xs transition-colors cursor-pointer self-start sm:self-auto"
          >
            <Printer className="h-3.5 w-3.5 text-slate-500" />
            <span>Print</span>
          </button> */}
        </div>

        {/* 4 KPI Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Card 1: Class & Section */}
          <div className="rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 p-3.5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200/70 dark:border-blue-800/60 flex items-center justify-center shrink-0">
              <GraduationCap className="h-4.5 w-4.5" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-slate-500 dark:text-slate-400  tracking-wide">
                Class & Section
              </span>
              <span className="text-sm sm:text-base font-bold text-slate-800 dark:text-white truncate mt-0.5">
                {details.className}
              </span>
            </div>
          </div>

          {/* Card 2: Academic Year */}
          <div className="rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 p-3.5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-400 border border-cyan-200/70 dark:border-cyan-800/60 flex items-center justify-center shrink-0">
              <Calendar className="h-4.5 w-4.5" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-slate-500 dark:text-slate-400  tracking-wide">
                Academic Year
              </span>
              <span className="text-sm sm:text-base font-bold text-slate-800 dark:text-white font-mono truncate mt-0.5">
                {details.academicYear}
              </span>
            </div>
          </div>

          {/* Card 3: Roll Number */}
          <div className="rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 p-3.5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/70 dark:border-purple-800/60 flex items-center justify-center shrink-0">
              <User className="h-4.5 w-4.5" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-slate-500 dark:text-slate-400  tracking-wide">
                Roll Number
              </span>
              <span className="text-sm sm:text-base font-bold text-slate-800 dark:text-white font-mono truncate mt-0.5">
                {details.rollNumber}
              </span>
            </div>
          </div>

          {/* Card 4: Admission Number */}
          <div className="rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 p-3.5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-pink-50 text-pink-600 dark:bg-pink-950/60 dark:text-pink-400 border border-pink-200/70 dark:border-pink-800/60 flex items-center justify-center shrink-0">
              <School className="h-4.5 w-4.5" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-slate-500 dark:text-slate-400  tracking-wide">
                Admission Number
              </span>
              <span className="text-sm sm:text-base font-bold text-slate-800 dark:text-white font-mono truncate mt-0.5">
                {details.admissionNumber}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Middle Grid: Subjects Table (Left 5 cols) + Academic Progress (Right 7 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* LEFT: Subjects Table (5 cols) */}
        <div className="lg:col-span-5 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs flex flex-col overflow-hidden">
          <div className="p-4 border-b border-slate-100 dark:border-white/10 flex items-center gap-2.5">
            <BookOpen className="h-4.5 w-4.5 text-indigo-600 dark:text-indigo-400" />
            <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              Subjects
            </h4>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[340px] text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100/80 dark:bg-[#0b1322] text-[13px] font-bold text-slate-700 dark:text-slate-300">
                  <th className="py-2.5 px-3.5 w-12 text-center">#</th>
                  <th className="py-2.5 px-3.5">Subject</th>
                  <th className="py-2.5 px-3.5">Teacher</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5 bg-white dark:bg-[#1b1d34]">
                {details.subjects.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-8 px-3.5 text-center text-sm font-medium text-slate-500 dark:text-slate-400"
                    >
                      Your school has not set up this class's subjects yet.
                    </td>
                  </tr>
                )}
                {details.subjects.map((sub) => {
                  const theme = getSubjectIconTheme(sub.name);
                  return (
                    <tr key={sub.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-2.5 px-3.5 text-center font-mono text-xs text-slate-400 dark:text-slate-500">
                        {sub.code}
                      </td>
                      <td className="py-2.5 px-3.5 font-bold text-slate-800 dark:text-white">
                        <div className="flex items-center gap-2.5">
                          <span className={cn('p-1.5 rounded-lg shrink-0', theme.box)}>
                            {theme.icon}
                          </span>
                          <span className="text-xs sm:text-sm font-bold truncate">{sub.name}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3.5 text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-medium truncate">
                        {sub.teacher ?? (
                          <span className="text-slate-400 dark:text-slate-500 italic">
                            Not allotted yet
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT: Academic Progress (7 cols) */}
        <div className="lg:col-span-7 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-4 flex flex-col gap-3.5">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-white/10 pb-3.5">
              <div className="flex items-center gap-2.5">
                <TrendingUp className="h-4.5 w-4.5 text-indigo-600 dark:text-indigo-400" />
                <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                  Academic Progress
                </h4>
              </div>

              {/* Sub-Tabs: Term Exams | Unit Tests | Assignments */}
              <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800/70 p-1 rounded-xl overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden max-w-full">
                {(['Term Exams', 'Unit Tests', 'Assignments'] as const).map((tab) => {
                  const isActive = activeSubTab === tab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={(e) => {
                        setActiveSubTab(tab);
                        e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      }}
                      className={cn(
                        'px-3 py-1 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap shrink-0',
                        isActive
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white',
                      )}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Score Breakdown Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100/80 dark:bg-[#0b1322] text-[13px] font-bold text-slate-700 dark:text-slate-300">
                    <th className="py-2.5 px-3">Exam</th>
                    <th className="py-2.5 px-3 text-center">Marks Obtained</th>
                    <th className="py-2.5 px-3 text-center">Total Marks</th>
                    <th className="py-2.5 px-3 text-center">Percentage</th>
                    <th className="py-2.5 px-3 text-center">Grade</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {examProgressList.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-8 px-3 text-center text-sm font-medium text-slate-500 dark:text-slate-400"
                      >
                        No {activeSubTab.toLowerCase()} have been marked yet.
                      </td>
                    </tr>
                  )}
                  {examProgressList.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-2.5 px-3 font-bold text-xs sm:text-sm text-slate-800 dark:text-white">
                        {row.exam}
                      </td>
                      <td className="py-2.5 px-3 text-center font-mono font-bold text-xs sm:text-sm text-slate-800 dark:text-white">
                        {row.marksObtained !== null ? row.marksObtained : '—'}
                      </td>
                      {/* Out of the papers that were scored — a dash, not 0, when none was. */}
                      <td className="py-2.5 px-3 text-center font-mono text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-medium">
                        {row.marksObtained !== null ? row.totalMarks : '—'}
                      </td>
                      <td className="py-2.5 px-3 text-center font-mono font-medium text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                        {row.percentage !== null ? `${row.percentage}%` : '—'}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {row.grade ? (
                          <span
                            className={cn(
                              'inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border',
                              GRADE_TONE_CLASSES[row.gradeTone ?? 'neutral'].badge,
                            )}
                          >
                            {row.grade}
                          </span>
                        ) : (
                          <span className="text-slate-400 font-mono">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Overall Performance Banner — nothing to average until a paper is marked */}
          {details.overallPerformance && (
          <div className="p-3.5 rounded-xl bg-slate-50/90 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between gap-4 mt-1">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200 dark:border-amber-800 flex items-center justify-center shrink-0">
                <Award className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-slate-900 dark:text-white">
                  Overall Performance
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  {details.overallPerformance.rating}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex flex-col items-end">
                <span className="text-xl font-black text-slate-900 dark:text-white leading-tight font-mono">
                  {details.overallPerformance.averageScore}%
                </span>
                <span className="text-[10px] text-slate-400 font-medium">Average Score</span>
              </div>
              <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200/70 dark:border-indigo-800/60 shrink-0">
                <TrendingUp className="h-4 w-4" />
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* 3. Bottom Row: Attendance Summary (Left 6 cols) + Remarks (Right 6 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Attendance Summary */}
        <div className="lg:col-span-6 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2.5 border-b border-slate-100 dark:border-white/10 pb-3.5">
            <Calendar className="h-4.5 w-4.5 text-indigo-600 dark:text-indigo-400" />
            <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              Attendance Summary
            </h4>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1 text-xs sm:text-sm">
            <div className="p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 flex flex-col">
              <span className="text-slate-500 dark:text-slate-400 font-medium">Total Working Days</span>
              <span className="text-lg sm:text-xl font-black text-slate-900 dark:text-white font-mono mt-1">
                {details.attendance.totalWorkingDays}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 flex flex-col">
              <span className="text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Days Present
              </span>
              <span className="text-lg sm:text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono mt-1">
                {details.attendance.daysPresent}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 flex flex-col">
              <span className="text-rose-700 dark:text-rose-400 font-medium flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-rose-500" /> Days Absent
              </span>
              <span className="text-lg sm:text-xl font-black text-rose-600 dark:text-rose-400 font-mono mt-1">
                {details.attendance.daysAbsent}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 flex flex-col">
              <span className="text-slate-500 dark:text-slate-400 font-medium">Attendance Percentage</span>
              <span className="text-lg sm:text-xl font-black text-indigo-600 dark:text-indigo-400 font-mono mt-1">
                {details.attendance.percentage === null
                  ? '—'
                  : `${details.attendance.percentage}%`}
              </span>
            </div>
          </div>
        </div>

        {/* Remarks Card */}
        <div className="lg:col-span-6 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-4 flex flex-col gap-3.5">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3.5">
            <div className="flex items-center gap-2.5">
              <MessageSquare className="h-4.5 w-4.5 text-indigo-600 dark:text-indigo-400" />
              <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Remarks
              </h4>
            </div>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              Latest
            </span>
          </div>

          {details.remarks ? (
            <div className="p-3.5 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-700/70 flex items-start gap-3">
              <Quote className="h-4.5 w-4.5 text-indigo-600 dark:text-indigo-400 shrink-0 rotate-180 mt-0.5" />
              <div className="flex flex-col">
                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 italic leading-relaxed">
                  "{details.remarks.quote}"
                </p>
                <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-slate-200/50 dark:border-slate-700/50 text-xs text-slate-500 dark:text-slate-400 font-semibold">
                  {details.remarks.author && <span>— {details.remarks.author}</span>}
                  <span className="ml-auto">{formatInstituteDate(details.remarks.date)}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="py-6 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
              {childName
                ? `No teacher has written a remark on ${childName}'s papers yet.`
                : 'Your teachers have not written a remark on a paper yet.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
